import React from "react";

/**
 * ZodiacHero — decorative rotating zodiac wheel with planets & constellations.
 * Pure SVG, no external deps, animated with CSS (see index.css .wheel-spin).
 */
export default function ZodiacHero() {
  const signs = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];
  const cx = 250, cy = 250, rOuter = 220, rInner = 180, rGlyph = 200;

  return (
    <div className="relative w-full max-w-[520px] aspect-square">
      {/* Outer glow */}
      <div className="absolute inset-6 rounded-full bg-[#10B981]/10 blur-3xl" />
      <div className="absolute inset-16 rounded-full bg-[#D4AF37]/15 blur-3xl" />

      {/* Rotating rings */}
      <svg viewBox="0 0 500 500" className="absolute inset-0 wheel-spin">
        <defs>
          <linearGradient id="ring-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0.6" />
          </linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke="url(#ring-g)" strokeWidth="1.2" strokeDasharray="2 6" />
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" />
        {/* Radial ticks */}
        {[...Array(60)].map((_, i) => {
          const a = (i / 60) * Math.PI * 2;
          const x1 = cx + Math.cos(a) * (rOuter - 6);
          const y1 = cy + Math.sin(a) * (rOuter - 6);
          const x2 = cx + Math.cos(a) * rOuter;
          const y2 = cy + Math.sin(a) * rOuter;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D4AF37" strokeOpacity={i % 5 === 0 ? "0.9" : "0.35"} strokeWidth="1" />;
        })}
        {/* Zodiac glyphs */}
        {signs.map((s, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(a) * rGlyph;
          const y = cy + Math.sin(a) * rGlyph;
          return (
            <text
              key={s}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#D4AF37"
              fontSize="22"
              fontFamily="serif"
              transform={`rotate(${(i / 12) * 360 + 90}, ${x}, ${y})`}
            >
              {s}
            </text>
          );
        })}
      </svg>

      {/* Counter-rotating inner ring */}
      <svg viewBox="0 0 500 500" className="absolute inset-0 wheel-spin-rev">
        <circle cx={cx} cy={cy} r="140" fill="none" stroke="#10B981" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="4 8" />
        <circle cx={cx} cy={cy} r="110" fill="none" stroke="#D4AF37" strokeOpacity="0.4" strokeWidth="1" />
        {/* House lines */}
        {[...Array(12)].map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const x1 = cx + Math.cos(a) * 110;
          const y1 = cy + Math.sin(a) * 110;
          const x2 = cx + Math.cos(a) * 140;
          const y2 = cy + Math.sin(a) * 140;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D4AF37" strokeOpacity="0.5" strokeWidth="1" />;
        })}
      </svg>

      {/* Static center */}
      <svg viewBox="0 0 500 500" className="absolute inset-0">
        {/* Constellation lines (Big Dipper style, decorative) */}
        <g stroke="#F5E8D8" strokeOpacity="0.4" strokeWidth="0.8" fill="none">
          <polyline points="120,110 155,140 195,130 230,155" />
          <polyline points="360,120 340,155 375,180 405,165" />
          <polyline points="90,340 130,360 160,340 200,375" />
        </g>
        {[
          [120,110],[155,140],[195,130],[230,155],
          [360,120],[340,155],[375,180],[405,165],
          [90,340],[130,360],[160,340],[200,375],
          [70,220],[420,240],[250,50],[250,450],
        ].map(([x,y], i) => (
          <circle key={i} cx={x} cy={y} r="1.6" fill="#F5E8D8" opacity="0.85" />
        ))}

        {/* Central Sun */}
        <circle cx={cx} cy={cy} r="72" fill="#0B1F3A" stroke="#D4AF37" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="62" fill="none" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="0.8" strokeDasharray="2 3" />
        {/* Sun rays */}
        {[...Array(12)].map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const x1 = cx + Math.cos(a) * 74;
          const y1 = cy + Math.sin(a) * 74;
          const x2 = cx + Math.cos(a) * 86;
          const y2 = cy + Math.sin(a) * 86;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" />;
        })}
        {/* Om / core mark */}
        <text x={cx} y={cy + 10} textAnchor="middle" fontFamily="serif" fontSize="46" fill="#D4AF37">ॐ</text>
      </svg>

      {/* Orbiting planet dots */}
      <div className="absolute inset-0" style={{ ["--orbit-r"]: "180px" }}>
        <div className="absolute top-1/2 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 orbit-dot" style={{ ["--orbit-d"]: "22s" }}>
          <div className="w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.8)]" />
        </div>
      </div>
      <div className="absolute inset-0" style={{ ["--orbit-r"]: "140px" }}>
        <div className="absolute top-1/2 left-1/2 w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 orbit-dot" style={{ ["--orbit-d"]: "16s" }}>
          <div className="w-2.5 h-2.5 rounded-full bg-[#10B981] shadow-[0_0_18px_rgba(16,185,129,0.8)]" />
        </div>
      </div>
      <div className="absolute inset-0" style={{ ["--orbit-r"]: "220px" }}>
        <div className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 orbit-dot" style={{ ["--orbit-d"]: "30s" }}>
          <div className="w-2 h-2 rounded-full bg-[#F5E8D8] shadow-[0_0_16px_rgba(245,232,216,0.9)]" />
        </div>
      </div>
    </div>
  );
}
